import { Dispatch, SetStateAction, useRef, useState } from "react"
import useSWRImmutable from "swr/immutable"

import { EVACDeviceFormData } from "../../../../../components/devices/lct/types/providerTypes"
import { LCTOnlyDatasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { getTsIoMapper } from "../../../../../utils/general/generalUtils"
import {
    getEmptyEVACDeviceFormData,
    mapEVACDeviceResponseToDeviceFormData,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import { getClientProtected } from "../../../clients/clients"
import { CONTINUE_DRAFT_CONFIG } from "../../../swrConfig"
import { ContinueEVACDraftResponse } from "../../../types/devices/lct/publishEvacDeviceTypes"
import { CONTINUE_LCT_DEVICE_DRAFT } from "../../../urls/private"

interface IUseContinueEVACDeviceDraftOutput {
    data: EVACDeviceFormData
    setData: Dispatch<SetStateAction<EVACDeviceFormData>>
    isLoading: boolean
    isValidating: boolean
    attachmentUUID: string | null
    apiResSystemRef: string | null
    error?: AppError
}

const continueEVACDeviceDraftMapper = getTsIoMapper(
    ContinueEVACDraftResponse,
    "continueEVACDeviceDraftMapper"
)

const useContinueEVACDeviceDraft = (
    deviceRef: string
): IUseContinueEVACDeviceDraftOutput => {
    const [data, setData] = useState<EVACDeviceFormData>(
        getEmptyEVACDeviceFormData()
    )
    const [isLoading, setIsLoading] = useState(true)

    // Only to be set in the SWR fetcher. Ref used as useState triggers the
    // re-render
    const attachmentUUID = useRef<string | null>(null)
    const apiResSystemRef = useRef<string | null>(null)

    const { error, isValidating } = useSWRImmutable(
        CONTINUE_LCT_DEVICE_DRAFT(deviceRef, LCTOnlyDatasets.EVAC),
        async (url: string): Promise<void> => {
            try {
                const response = await getClientProtected(url)
                const mappedRes = continueEVACDeviceDraftMapper.map(response)
                attachmentUUID.current = mappedRes.Attachment_UUID
                apiResSystemRef.current = mappedRes.SystemReference
                setData(mapEVACDeviceResponseToDeviceFormData(mappedRes))
            } catch {
                throw invalidApiDataError("Failed to continue draft version")
            } finally {
                setIsLoading(false)
            }
        },
        CONTINUE_DRAFT_CONFIG
    )

    return {
        data,
        setData,
        isLoading,
        isValidating,
        attachmentUUID: attachmentUUID.current,
        apiResSystemRef: apiResSystemRef.current,
        error,
    }
}

export default useContinueEVACDeviceDraft
